import { type ReactNode } from 'react';
import { GroupIcon } from 'lucide-react';
import { FilterChipPopover, SortChipPopover, type SortChipOption } from '@/shared/ui';
import { SearchInput } from '@/shared/ui/SearchInput';
import { briefSortChoices } from '../../lib/sort-choices';
import type { FilterColumnSpec, FilterRule, FilterValueCounts } from '../../lib/filtering';
import { TrackersFilterPanel } from '../TrackersFilterPanel';
import { useTranslation } from 'react-i18next';

/** One selectable grouping in the Group-by chip. */
export interface GroupByOption {
  value: string;
  label: string;
}

/**
 * What the filter panel needs on top of the columns and selections: the
 * per-value counts it prints on every chip, the summary line's numbers, and
 * the recently-used column memory.
 */
export interface FilterPanelProps {
  /** Remount key: the panel's quick/advanced mode is seeded from the rules
   *  persisted for one tracker, and has to be re-seeded for the next. */
  trackerId: string;
  /** Left out by a board with no client-side rules engine; see the panel. */
  rules?: readonly FilterRule[];
  onRulesChange?: (next: readonly FilterRule[]) => void;
  /** Selections the board is applying — what the trigger badge counts. */
  activeFilterCount: number;
  counts: FilterValueCounts;
  /** Left out by a board that never learns its unnarrowed total. */
  visibleItems?: number;
  totalItems?: number;
  recentColumnIds: readonly string[];
  onColumnUsed: (columnId: string) => void;
}

interface TrackersToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  sortId: string | null;
  onSortChange: (next: string | null) => void;
  /** Sort options; defaults to the briefs board's choices. */
  sortOptions?: ReadonlyArray<SortChipOption>;
  /** Group-by chip (rendered right after Sort). Omit to hide. */
  groupByOptions?: ReadonlyArray<GroupByOption>;
  groupByValue?: string | null;
  onGroupByChange?: (next: string | null) => void;
  /**
   * Dynamic filters. Users add filters by column from a "Filter" menu; each
   * active filter renders as a multi-select chip. `filters` maps a column id
   * to its selected values (presence of the key = the filter is shown).
   */
  filterColumns?: ReadonlyArray<FilterColumnSpec>;
  filters?: Record<string, readonly string[]>;
  onFiltersChange?: (next: Record<string, readonly string[]>) => void;
  filterPanel: FilterPanelProps;
  /** Rendered first in the row, ahead of the search box. */
  leading?: ReactNode;
  /** Rendered at the right end of the toolbar row (e.g. the view switcher). */
  trailing?: ReactNode;
}

export function TrackersToolbar(props: TrackersToolbarProps) {
  const { t } = useTranslation('trackers');

  const {
    searchValue,
    onSearchChange,
    sortId,
    onSortChange,
    sortOptions,
    groupByOptions,
    groupByValue,
    onGroupByChange,
    filterColumns,
    filters,
    onFiltersChange,
    filterPanel,
    leading,
    trailing,
  } = props;

  const showFilters =
    Boolean(filterColumns?.length) && Boolean(filters) && Boolean(onFiltersChange);

  return (
    // `items-start` + a flex-1 wrapping left column keeps the view switcher
    // pinned top-right no matter how many filter chips accrue: the chips wrap
    // DOWN within the left column instead of pushing the switcher onto its own
    // line (which flex-wrap + justify-between used to do).
    <div className="flex items-start gap-3 p-4">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        {leading}
        <SearchInput
          value={searchValue}
          onChange={onSearchChange}
          placeholder={t('trackersBoard.trackersToolbar.search')}
          aria-label={t('trackersBoard.trackersToolbar.searchLabel')}
          className="w-70"
        />

        <SortChipPopover
          options={sortOptions ?? briefSortChoices()}
          value={sortId}
          onChange={onSortChange}
          showTriggerLabel
          triggerAriaLabel={t('trackersBoard.trackersToolbar.sortItems')}
        />

        {groupByOptions && onGroupByChange ? (
          <FilterChipPopover<string>
            label={t('trackersBoard.trackersToolbar.groupBy')}
            value={groupByValue ?? null}
            options={groupByOptions.map((o) => ({ value: o.value, label: o.label }))}
            onChange={onGroupByChange}
            leadingIcon={
              <GroupIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            }
            // A board is ALWAYS grouped (no headerless state), so there's no
            // "None" reset — the user switches between groupings, never off.
            hideReset
            triggerAriaLabel={t('trackersBoard.trackersToolbar.groupItems')}
          />
        ) : null}

        {showFilters ? (
          <TrackersFilterPanel
            key={filterPanel.trackerId}
            columns={filterColumns!}
            filters={filters!}
            onFiltersChange={onFiltersChange!}
            {...filterPanel}
          />
        ) : null}
      </div>
      <div className="flex items-center gap-3">{trailing}</div>
    </div>
  );
}
